import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    Search, ClipboardCheck, AlertTriangle, Home as HomeIcon, Phone, MapPin, User2,
    Users, Tag, CalendarClock, CalendarCheck2, Infinity as InfinityIcon,
} from "lucide-react";
import Spinner from "../Spinner/Spinner";
import Breadcrumb from "../Breadcrumbs/Breadcrumbs";
import NewBadge from "../NewBadge/NewBadge";
import { getActivePartnerships } from "../../services/partnershipsApi";
import { getCategories } from "../../services/categoriesApi";
import { getProviders } from "../../services/providersApi";
import { getPhoto } from "../../storageService";
import { useAuth } from "../../utils/AuthContext";
import { useTheme } from "../../utils/ThemeContext";
import "../Providers/PartnerCard.css";
import "./PartnerPanelPage.css";

const BYPASS_PARTNER_GATE = false;

/**
 * Panel privado del Partner: ve sus propios beneficios (read-only) y puede
 * buscar/filtrar proveedores de servicios de las categorías que sponsorea,
 * para verificar la identidad de quien le pide el beneficio en el local.
 */
const PartnerPanelPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { theme } = useTheme();
    const userData = useMemo(() => {
        try { return JSON.parse(localStorage.getItem("userData")); }
        catch { return null; }
    }, []);

    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [partnership, setPartnership] = useState(null);
    const [bannerUrl, setBannerUrl] = useState(null);
    const [categoryNameById, setCategoryNameById] = useState({});

    // Búsqueda
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [keyword, setKeyword] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // Stats
    const [eligibleByCategoryId, setEligibleByCategoryId] = useState({});
    const [eligibleLoading, setEligibleLoading] = useState(false);

    // ============= Carga inicial =============

    useEffect(() => {
        if (!user || !userData?.providerRef) {
            setAuthError("Tenés que iniciar sesión con un usuario proveedor para acceder al Panel Partner.");
            setLoading(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const [allPartnerships, serviceCats] = await Promise.all([
                    getActivePartnerships(),
                    getCategories("servicios"),
                ]);
                if (cancelled) return;

                const mine = (allPartnerships || []).find(
                    (p) => (p.productProviderId || "").toLowerCase() ===
                           (userData.providerRef || "").toLowerCase()
                );
                if (!mine) {
                    setAuthError("Tu usuario no es Partner activo. Si te registraste recientemente, contactá con el admin.");
                    return;
                }

                const nameMap = {};
                (serviceCats || []).forEach((c) => {
                    if (c?.id && c?.name) nameMap[c.id] = c.name;
                });
                setCategoryNameById(nameMap);
                setPartnership(mine);

                // Selección inicial: primera categoría sponsoreada (si hay)
                const first = (mine.sponsoredServiceCategories || [])[0];
                if (first) setSelectedCategoryId(first);

                if (mine.bannerPhoto) {
                    try { setBannerUrl(await getPhoto(mine.bannerPhoto)); }
                    catch { setBannerUrl(null); }
                }
            } catch (err) {
                console.error("[PartnerPanel] error cargando partnership:", err);
                setAuthError("Ocurrió un error cargando el panel. Probá nuevamente.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user, userData?.providerRef]);

    // ============= Stats: profesionales elegibles por categoría =============

    useEffect(() => {
        if (!partnership) return;
        const ids = partnership.sponsoredServiceCategories || [];
        if (ids.length === 0) return;

        let cancelled = false;
        setEligibleLoading(true);
        (async () => {
            const map = {};
            await Promise.all(ids.map(async (id) => {
                const name = categoryNameById[id];
                if (!name) { map[id] = 0; return; }
                try {
                    const data = await getProviders(
                        { categoryName: name, location: "TODAS", keywords: [] },
                        "service",
                        500,
                        null,
                    );
                    map[id] = Array.isArray(data?.providers) ? data.providers.length : 0;
                } catch {
                    map[id] = 0;
                }
            }));
            if (!cancelled) {
                setEligibleByCategoryId(map);
                setEligibleLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [partnership, categoryNameById]);

    const stats = useMemo(() => {
        if (!partnership) return null;
        const sponsoredCount = (partnership.sponsoredServiceCategories || []).length;
        const totalEligible = Object.values(eligibleByCategoryId).reduce((a, b) => a + b, 0);

        const now = Date.now();
        const daysActive = partnership.startDate
            ? Math.max(0, Math.floor((now - partnership.startDate) / 86400000))
            : null;

        let expiry;
        if (!partnership.endDate) {
            expiry = { label: "Sin vencimiento", value: "∞", icon: "infinity" };
        } else {
            const days = Math.ceil((partnership.endDate - now) / 86400000);
            if (days < 0) expiry = { label: "Vencido", value: `${Math.abs(days)} d`, icon: "warn" };
            else if (days === 0) expiry = { label: "Vence hoy", value: "0 d", icon: "warn" };
            else expiry = { label: "Vence en", value: `${days} d`, icon: "calendar" };
        }

        return { sponsoredCount, totalEligible, daysActive, expiry };
    }, [partnership, eligibleByCategoryId]);

    // ============= Búsqueda de profesionales =============

    const handleSearch = useCallback(async (e) => {
        if (e) e.preventDefault();
        if (!selectedCategoryId) return;

        const categoryName = categoryNameById[selectedCategoryId];
        if (!categoryName) return;

        setSearching(true);
        setHasSearched(true);
        try {
            const filters = {
                categoryName,
                location: "TODAS",
                keywords: keyword.trim() ? [keyword.trim()] : [],
            };
            const data = await getProviders(filters, "service", 50, null);
            const providers = Array.isArray(data?.providers) ? data.providers : [];

            // Resolver fotos en paralelo (best effort)
            const withPhotos = await Promise.all(providers.map(async (p) => {
                if (!p.photo) return { ...p, photoUrl: null };
                try { return { ...p, photoUrl: await getPhoto(p.photo) }; }
                catch { return { ...p, photoUrl: null }; }
            }));

            // Si hay keyword también filtramos client-side por username/name (por si el backend
            // matchea por subcategorías y queremos resaltar matches exactos del nombre).
            const term = keyword.trim().toLowerCase();
            const filtered = term
                ? withPhotos.filter((p) =>
                    (p.username || "").toLowerCase().includes(term) ||
                    (p.name || "").toLowerCase().includes(term))
                : withPhotos;

            setResults(filtered);
        } catch (err) {
            console.error("[PartnerPanel] error en búsqueda:", err);
            setResults([]);
        } finally {
            setSearching(false);
        }
    }, [selectedCategoryId, categoryNameById, keyword]);

    // ============= Render: estados de error / loading =============

    if (loading) {
        return (
            <div className={`partner-panel ${theme === "light" ? "light-theme" : "dark-theme"}`}>
                <Spinner />
            </div>
        );
    }

    if (authError || !partnership) {
        return (
            <div className={`partner-panel ${theme === "light" ? "light-theme" : "dark-theme"}`}>
                <div className="partner-panel__error">
                    <AlertTriangle size={48} className="partner-panel__error-icon" />
                    <h2>Acceso restringido</h2>
                    <p>{authError || "No se encontró un partnership activo para tu cuenta."}</p>
                    <button className="partner-panel__error-btn" onClick={() => navigate("/")}>
                        Volver al inicio
                    </button>
                </div>
            </div>
        );
    }

    const sponsoredIds = partnership.sponsoredServiceCategories || [];

    // ============= Render principal =============

    return (
        <div className={`partner-panel ${theme === "light" ? "light-theme" : "dark-theme"}`}>
            <Helmet>
                <title>Panel Partner | ContactaMe</title>
                <meta name="robots" content="noindex" />
            </Helmet>

            <Breadcrumb items={[
                { label: "Inicio", icon: <HomeIcon size={16} />, link: "/" },
                { label: "Partners", link: "/partners" },
                { label: "Panel" },
            ]} />

            {/* ===== Stats / KPIs (full-width arriba) ===== */}
            {stats && (
                <section className="partner-panel__stats" aria-label="Resumen del partnership">
                    <div className="partner-panel__stat">
                        <span className="partner-panel__stat-icon"><Users size={18} /></span>
                        <div className="partner-panel__stat-body">
                            <span className="partner-panel__stat-label">Profesionales elegibles</span>
                            <span className="partner-panel__stat-value">
                                {eligibleLoading
                                    ? <span className="partner-panel__stat-skeleton" />
                                    : stats.totalEligible.toLocaleString("es-AR")}
                            </span>
                            <span className="partner-panel__stat-hint">
                                en {stats.sponsoredCount} categoría{stats.sponsoredCount === 1 ? "" : "s"}
                            </span>
                        </div>
                    </div>

                    <div className="partner-panel__stat">
                        <span className="partner-panel__stat-icon"><Tag size={18} /></span>
                        <div className="partner-panel__stat-body">
                            <span className="partner-panel__stat-label">Categorías sponsoreadas</span>
                            <span className="partner-panel__stat-value">{stats.sponsoredCount}</span>
                            <span className="partner-panel__stat-hint">de servicios</span>
                        </div>
                    </div>

                    <div className="partner-panel__stat">
                        <span className="partner-panel__stat-icon"><CalendarCheck2 size={18} /></span>
                        <div className="partner-panel__stat-body">
                            <span className="partner-panel__stat-label">Días activo</span>
                            <span className="partner-panel__stat-value">
                                {stats.daysActive == null ? "—" : stats.daysActive.toLocaleString("es-AR")}
                            </span>
                            <span className="partner-panel__stat-hint">
                                {partnership.startDate
                                    ? `desde ${new Date(partnership.startDate).toLocaleDateString("es-AR")}`
                                    : "fecha sin registrar"}
                            </span>
                        </div>
                    </div>

                    <div className={`partner-panel__stat ${stats.expiry.icon === "warn" ? "partner-panel__stat--warn" : ""}`}>
                        <span className="partner-panel__stat-icon">
                            {stats.expiry.icon === "infinity"
                                ? <InfinityIcon size={18} />
                                : <CalendarClock size={18} />}
                        </span>
                        <div className="partner-panel__stat-body">
                            <span className="partner-panel__stat-label">{stats.expiry.label}</span>
                            <span className="partner-panel__stat-value">{stats.expiry.value}</span>
                            <span className="partner-panel__stat-hint">
                                {partnership.endDate
                                    ? new Date(partnership.endDate).toLocaleDateString("es-AR")
                                    : "vigencia abierta"}
                            </span>
                        </div>
                    </div>
                </section>
            )}

            {/* ===== Bottom: Búsqueda (2/3) + Partnership (1/3) ===== */}
            <div className="partner-panel__bottom">
            <section className="partner-panel__search-section">
                <h2 className="partner-panel__section-title">
                    <ClipboardCheck size={18} />
                    Verificar profesional
                </h2>
                <p className="partner-panel__section-intro">
                    Buscá entre los profesionales registrados en las categorías que sponsoreás para
                    confirmar la identidad de quien te solicita el beneficio.
                </p>

                <form className="partner-panel__form" onSubmit={handleSearch}>
                    <label className="partner-panel__field">
                        <span>Categoría</span>
                        <select
                            value={selectedCategoryId}
                            onChange={(e) => setSelectedCategoryId(e.target.value)}
                        >
                            {sponsoredIds.map((id) => (
                                <option key={id} value={id}>
                                    {categoryNameById[id] || id}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="partner-panel__field partner-panel__field--grow">
                        <span>Nombre o usuario</span>
                        <input
                            type="text"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder="Buscar por nombre o username..."
                        />
                    </label>
                    <button
                        type="submit"
                        className="partner-panel__search-btn"
                        disabled={searching || !selectedCategoryId}
                    >
                        <Search size={16} />
                        {searching ? "Buscando..." : "Buscar"}
                    </button>
                </form>

                {/* Resultados */}
                <div className="partner-panel__results">
                    {searching ? (
                        <p className="partner-panel__results-empty">Buscando profesionales...</p>
                    ) : !hasSearched ? (
                        <p className="partner-panel__results-empty">
                            Elegí una categoría y presioná <b>Buscar</b> para ver los profesionales.
                        </p>
                    ) : results.length === 0 ? (
                        <p className="partner-panel__results-empty">
                            No se encontraron profesionales con esos criterios.
                        </p>
                    ) : (
                        <ul className="partner-panel__results-list">
                            {results.map((p) => (
                                <li key={p.username} className="partner-panel__result">
                                    <Link to={`/${p.username}`} className="partner-panel__result-link">
                                        {p.photoUrl ? (
                                            <img
                                                src={p.photoUrl}
                                                alt={p.name}
                                                className="partner-panel__result-photo"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="partner-panel__result-photo partner-panel__result-photo--plain">
                                                <User2 size={22} />
                                            </div>
                                        )}
                                        <div className="partner-panel__result-body">
                                            <span className="partner-panel__result-name">{p.name || p.username}</span>
                                            <span className="partner-panel__result-username">@{p.username}</span>
                                            <div className="partner-panel__result-meta">
                                                {p.phone && (
                                                    <span className="partner-panel__result-meta-item">
                                                        <Phone size={12} /> {p.phone}
                                                    </span>
                                                )}
                                                {p.locations?.[0] && (
                                                    <span className="partner-panel__result-meta-item">
                                                        <MapPin size={12} /> {p.locations[0]}
                                                        {p.locations.length > 1 ? ` +${p.locations.length - 1}` : ""}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <header className="partner-panel__header">
                <NewBadge label="PARTNER" size="md" className="partner-badge" />
                {bannerUrl && (
                    <div
                        className="partner-panel__banner"
                        style={{ backgroundImage: `url(${bannerUrl})` }}
                        aria-hidden="true"
                    />
                )}
                <div className="partner-panel__header-body">
                    <span className="partner-panel__eyebrow">
                        Tus beneficios como Partner de <span className="brand">CONTACTAME</span>
                    </span>
                    <h1 className="partner-panel__title">
                        {partnership.partnerName || partnership.productProviderId}
                    </h1>
                    {partnership.description && (
                        <p className="partner-panel__description">{partnership.description}</p>
                    )}
                    {sponsoredIds.length > 0 && (
                        <div className="partner-panel__sponsored">
                            <span className="partner-panel__sponsored-label">Sponsoreás:</span>
                            <ul className="partner-panel__chip-list">
                                {sponsoredIds.map((id) => (
                                    <li key={id} className="partner-panel__chip">
                                        {categoryNameById[id] || id}
                                        {typeof eligibleByCategoryId[id] === "number" && (
                                            <span className="partner-panel__chip-count">
                                                {eligibleByCategoryId[id]}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <p className="partner-panel__readonly-hint">
                        Esta información es de sólo lectura. Para modificarla, contactá con el admin.
                    </p>
                </div>
            </header>
            </div>
        </div>
    );
};

export default PartnerPanelPage;
